/**
 * Mandatory tri-state Yes/No requirement select (NCR Liability actions row).
 *
 * Tri-state: `null` = unselected ("Please Select"), so "No" (false) is a
 * distinct, retained choice rather than collapsing back to unselected.
 * Surfaces a backend/validation error (red border + inline, accessibly
 * announced message + aria-invalid/aria-describedby) when `error` is set,
 * mirroring the other NCR fields.
 *
 * Extracted from NCRDetailsForm.tsx to keep that file under the 500-line limit.
 */
import { Label } from '../../ui/label';

interface YesNoSelectProps {
  /** Stable id — used for the label association + aria-describedby wiring. */
  id: string;
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  /** Backend/validation error message; renders the field in its error state. */
  error?: string;
}

export default function YesNoSelect({ id, label, value, onChange, error }: YesNoSelectProps) {
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="flex flex-col">
      {/* min-h reserves two lines: the longest labels ("Replacement Required")
          wrap at narrow widths, and without a reserved height each select
          starts at a different vertical offset — the row looked ragged. */}
      <Label htmlFor={id} className="text-xs block leading-tight min-h-[2rem]">{label} <span className="text-red-500">*</span></Label>
      <select
        id={id}
        className={`w-full h-9 px-3 rounded-md border bg-background text-sm ${error ? 'border-red-500' : 'border-input'}`}
        value={value === null ? '' : value ? 'yes' : 'no'}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'yes')}
        aria-invalid={!!error || undefined}
        aria-describedby={errorId}
      >
        <option value="">Please Select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      {error && <p id={errorId} role="alert" className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
